import { Fragment, useState, useCallback } from 'react';
import { useParams, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, DamageMarkings, EmptyState, ErrorState, MediaOriginTag, MediaVideo, damageMarkingsAspect } from '@mustauto/ui/web';
import {
  hasPanelItems,
  isChecklistRef,
  isOSClosed,
  canEditInternalNotes,
  isPanelRef,
  executionMediaLabel,
  splitBeforeAfter,
  panelItemGroups,
  panelItemLabel,
  panelPhotoLabel,
  parseMarkingsJson,
  partOriginText,
  stageMediaRef,
  formatDateBR,
  formatWarrantyMonths,
  warrantyRevisionDates,
  roundStockQuantity,
  mediaOriginLabel,
} from '@mustauto/utils';
import {
  PART_ORIGINS,
  VEHICLE_PANELS,
  VEHICLE_PANEL_REGIONS,
  VEHICLE_PANEL_REGION_LABELS,
  getModuleLabel,
  getPartOriginLabel,
  getStageLabel,
  getVehiclePanelLabel,
  isPanelOperation,
  isVehiclePanel,
  panelItemDescription,
  vehicleSizeLabel,
} from '@mustauto/types';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@mustauto/ui/web';
import { ChatPanel } from '@/components/ChatPanel';
import { StockPartPicker, type PickedStockPart } from '@/components/StockPartPicker';
import { useServiceOrder, useAuditLog, useUncancelOS, type OSItem, type AuditLogEntry } from '@/hooks/useServiceOrders';
import { useAuth } from '@/hooks/useAuth';
import { useOSRealtime } from '@/hooks/useOSRealtime';
import { STATUS_MAP } from '@/lib/status';
import { formatReais, formatCentavos } from '@/lib/utils';
import { api } from '@/lib/api';

/** Item em edição: o que a API conhece, mais o saldo de estoque no momento da
 *  escolha — usado só para limitar a quantidade na tela. */
type DraftItem = Omit<OSItem, 'total'> & { stockAvailable?: number };
import { InviteLinkButton } from '@/components/InviteLinkButton';
import { PrintOSButton } from '@/components/PrintOSButton';
import { ReopenOSButton } from '@/components/ReopenOSButton';
import { QuoteResponseButtons } from '@/components/QuoteResponseButtons';
import { quoteResponseRecordedLine, type QuoteResponseChannel } from '@mustauto/types/quote-response';
import { OSStageSelect } from '@/components/OSStageSelect';
import { OSPaintField } from '@/components/OSPaintField';
import { OSSlotField } from '@/components/OSSlotField';
import { OSDeadlineField, formatDeadline } from '@/components/OSDeadlineField';
import { OSVehicleFields } from '@/components/OSVehicleFields';
import { OSPaintReadingsCard } from '@/components/OSPaintReadingsCard';
import { OSBeforeAfterCard } from '@/components/OSBeforeAfterCard';
import { OSConsumablesCard } from '@/components/OSConsumablesCard';
import { OSInternalNotesCard } from '@/components/OSInternalNotesCard';
import { InsuranceClaimCard } from '@/components/InsuranceClaimCard';
import { useCanViewFinance } from '@/hooks/useCanViewFinance';
import { useCan } from '@/hooks/useCan';
import { WhoCanHint } from '@/components/WhoCanHint';
import { DeliverOSButton } from '@/components/DeliverOSButton';
import { statusChangePermissions } from '@mustauto/types/permissions';
import { Breadcrumbs } from '@mustauto/ui/web';
import { ArrowLeft, ArrowRightLeft, CalendarClock, Car, CircleCheck, Clock, Download, History, Image, Layers, Loader2, NotebookPen, Package, PackageSearch, PaintBucket, ParkingSquare, Plus, RotateCcw, Save, Send, Trash2, Undo2, XCircle } from 'lucide-react';
import { SOFT_BADGE_CLASSES } from '@mustauto/ui/web';
import { useStageConfig } from '@/hooks/useSettings';

/**
 * Baixa uma mídia da OS.
 *
 * A API devolve uma presigned URL com `Content-Disposition: attachment` — sem
 * isso o navegador só navegaria até a imagem, porque o atributo `download` de
 * um link é ignorado em cross-origin. O arquivo vem direto do S3; a API só
 * assina a URL e monta o nome legível.
 */
function MediaDownloadButton({ mediaId, label }: { mediaId: string; label: string }) {
  const [busy, setBusy] = useState(false);

  const handleDownload = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/media/${mediaId}/download`);
      // Âncora temporária em vez de window.open: não é bloqueada como popup,
      // e o attachment faz o navegador baixar sem sair da página.
      const link = document.createElement('a');
      link.href = data.url as string;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      // Falha de download não pode passar em silêncio.
      window.alert('Não foi possível baixar o arquivo. Tente novamente.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={busy}
      title={`Baixar ${label}`}
      aria-label={`Baixar ${label}`}
      className="absolute left-1 top-1 rounded bg-black/60 p-1.5 text-white opacity-0 transition-opacity hover:bg-black/80 focus:opacity-100 disabled:opacity-50 group-hover:opacity-100"
    >
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
    </button>
  );
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  pix: 'PIX',
  credit_card: 'Cartão de Crédito',
  debit_card: 'Cartão de Débito',
  cash: 'Dinheiro',
  bank_transfer: 'Transferência',
  other: 'Outro',
};

const STATUS_OPTIONS = [
  'in_analysis',
  'in_execution',
  'waiting_part',
  'outsourced',
  'completed',
  'waiting_payment',
  'cancelled',
] as const;

interface MediaItem {
  id: string;
  type: 'photo' | 'video';
  url: string;
  itemRef: string | null;
  visibility: string;
  createdAt: string;
  /** Só a foto de peça (`panel:`) traz — os seis ângulos vêm do jsonb do checklist. */
  annotationText?: string | null;
  markingsJson?: string | null;
  /** `'gallery'` = escolhida na galeria do aparelho (selo "Galeria"); nulo = câmera. */
  source?: string | null;
  takenAt?: string | null;
  /** Vídeo: `processing` enquanto o da galeria é convertido no servidor; e o pôster. */
  processing?: string | null;
  posterUrl?: string | null;
}

/** Metadados do JSONB os_checklist.photos (markings, anotações). */
interface ChecklistPhotoMeta {
  angle: string;
  s3Key?: string;
  markingsJson?: string;
  annotationText?: string;
}

const ANGLE_LABELS: Record<string, string> = {
  front: 'Frente',
  right: 'Lateral direita',
  left: 'Lateral esquerda',
  rear: 'Traseira',
  dashboard: 'Painel',
  trunk: 'Porta-malas',
};

function checklistLabel(angle: string): string {
  if (isPanelRef(angle)) return panelPhotoLabel(angle);
  return ANGLE_LABELS[angle] ?? angle;
}

interface QuoteRow {
  id: string;
  osId: string;
  total: string;
  validUntil: string | null;
  status: string;
  sentAt: string | null;
  respondedAt: string | null;
  /** `app` = o cliente pelo app; o resto = a oficina registrou. Nulo se pendente. */
  responseChannel: QuoteResponseChannel | null;
  respondedByName: string | null;
  whatsappMessageId: string | null;
  whatsappStatus: string | null;
}

const QUOTE_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  approved: 'Aprovado',
  rejected: 'Recusado',
  expired: 'Expirado',
};

const WHATSAPP_STATUS_BADGE: Record<string, { icon: string; label: string; className: string }> = {
  sent: { icon: '\uD83D\uDCE4', label: 'Enviado', className: SOFT_BADGE_CLASSES.blue },
  delivered: { icon: '\uD83D\uDCEC', label: 'Entregue', className: SOFT_BADGE_CLASSES.emerald },
  read: { icon: '\uD83D\uDC41', label: 'Lido', className: SOFT_BADGE_CLASSES.emerald },
  failed: { icon: '\u274C', label: 'Falhou', className: SOFT_BADGE_CLASSES.red },
};

function formatAction(action: string): { label: string; icon: typeof Clock } {
  switch (action) {
    case 'status_changed':
      return { label: 'Alterou status', icon: ArrowRightLeft };
    case 'cancelled':
      return { label: 'Cancelou OS', icon: XCircle };
    case 'uncancelled':
      return { label: 'Recuperou OS', icon: Undo2 };
    // O gestor desfez o fechamento por engano (até 48h depois).
    case 'reopened':
      return { label: 'Reabriu OS', icon: RotateCcw };
    // Correção feita pela equipe MustAuto no backoffice, a pedido da oficina.
    case 'status_corrected':
      return { label: 'Suporte MustAuto corrigiu o status', icon: RotateCcw };
    // Etapa dentro do módulo (funilaria, estética) — pelo app ou por aqui.
    case 'stage_changed':
      return { label: 'Mudou a etapa', icon: Layers };
    // O carro mudou de vaga — pelo app ou por aqui.
    case 'slot_changed':
      return { label: 'Trocou a vaga', icon: ParkingSquare };
    // Placa ou modelo corrigido (digitado errado na abertura).
    case 'vehicle_changed':
      return { label: 'Corrigiu o veículo', icon: Car };
    // Prazo de entrega mudado — pelo app ou por aqui.
    case 'deadline_updated':
      return { label: 'Mudou o prazo', icon: CalendarClock };
    // Anotações internas — pelo app ou por aqui. O texto de antes fica aqui.
    case 'internal_notes_updated':
      return { label: 'Editou as anotações internas', icon: NotebookPen };
    // Cor, código ou fórmula da tinta (F3.6).
    case 'paint_updated':
      return { label: 'Atualizou a pintura', icon: PaintBucket };
    case 'quote_sent':
      return { label: 'Enviou orçamento', icon: Send };
    // Resposta que chegou por fora do app (WhatsApp, ligação, balcão),
    // registrada pela oficina — o canal vai no motivo.
    case 'quote_approved':
      return { label: 'Registrou: cliente aprovou o orçamento', icon: CircleCheck };
    case 'quote_rejected':
      return { label: 'Registrou: cliente recusou o orçamento', icon: XCircle };
    case 'quote_approved_by_client':
      return { label: 'Cliente aprovou o orçamento pelo app', icon: CircleCheck };
    case 'quote_rejected_by_client':
      return { label: 'Cliente recusou o orçamento pelo app', icon: XCircle };
    default:
      return { label: action, icon: Clock };
  }
}

function AuditTimeline({ entries }: { entries: AuditLogEntry[] }) {
  // O histórico guarda a CHAVE da etapa; o nome é o de hoje (F3.8) — renomear
  // a cabine para "Estufa" renomeia também as linhas antigas.
  const stageConfig = useStageConfig();
  if (entries.length === 0) {
    return (
      <EmptyState
        icon={<History size={28} />}
        title="Nenhum registro de histórico"
        description="Mudanças de status, itens e pagamentos desta OS aparecem aqui em ordem cronológica."
      />
    );
  }

  return (
    <div className="relative space-y-0">
      {/* Vertical line */}
      <div className="absolute left-4 top-2 bottom-2 w-px bg-border" />

      {entries.map((entry) => {
        const { label, icon: Icon } = formatAction(entry.action);
        // Na troca de etapa, de/para são chaves de etapa — procurá-las no mapa
        // de status só não acharia nada ("funilaria" é etapa E módulo).
        const isStage = entry.action === 'stage_changed';
        // Na troca de vaga, de/para são os nomes das vagas.
        const isSlot = entry.action === 'slot_changed';
        // Na correção do veículo, de/para são "placa · marca modelo ano".
        const isVehicle = entry.action === 'vehicle_changed';
        // No prazo, de/para são instantes ISO. O registro antigo (antes do
        // `fromValue`) só tem o destino.
        const isDeadline = entry.action === 'deadline_updated';
        // Nas anotações, de/para são textos livres: mostra o de antes, para a
        // anotação apagada por engano ser recuperável.
        const isNotes = entry.action === 'internal_notes_updated';
        const plainValues = isStage || isSlot || isVehicle || isDeadline || isNotes;
        const fromCfg = !plainValues && entry.fromValue ? STATUS_MAP[entry.fromValue] : null;
        const toCfg = !plainValues && entry.toValue ? STATUS_MAP[entry.toValue] : null;

        return (
          <div key={entry.id} className="relative flex gap-4 py-3 pl-10">
            {/* Dot */}
            <div className="absolute left-2.5 top-4.5 flex h-3 w-3 items-center justify-center rounded-full border-2 border-primary bg-background" />

            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2 text-sm">
                <Icon size={14} className="text-muted-foreground" />
                <span className="font-medium">{label}</span>
              </div>

              {(fromCfg || toCfg) && (
                <div className="flex items-center gap-2 text-xs">
                  {fromCfg && (
                    <Badge variant={fromCfg.variant}>{fromCfg.label}</Badge>
                  )}
                  {fromCfg && toCfg && (
                    <span className="text-muted-foreground">→</span>
                  )}
                  {toCfg && (
                    <Badge variant={toCfg.variant}>{toCfg.label}</Badge>
                  )}
                </div>
              )}

              {isStage && (
                <p className="text-xs text-muted-foreground">
                  {entry.fromValue ? `${getStageLabel(entry.fromValue, stageConfig)} → ` : ''}
                  <span className="font-medium text-foreground">
                    {entry.toValue ? getStageLabel(entry.toValue, stageConfig) : 'Sem etapa'}
                  </span>
                </p>
              )}

              {isSlot && (
                <p className="text-xs text-muted-foreground">
                  {entry.fromValue ? `${entry.fromValue} → ` : 'Sem vaga → '}
                  <span className="font-medium text-foreground">{entry.toValue ?? 'Sem vaga'}</span>
                </p>
              )}

              {isVehicle && (
                <p className="text-xs text-muted-foreground">
                  {entry.fromValue ? `${entry.fromValue} → ` : ''}
                  <span className="font-medium text-foreground">{entry.toValue}</span>
                </p>
              )}

              {isDeadline && entry.toValue && (
                <p className="text-xs text-muted-foreground">
                  {entry.fromValue ? `${formatDeadline(entry.fromValue)} → ` : ''}
                  <span className="font-medium text-foreground">{formatDeadline(entry.toValue)}</span>
                </p>
              )}

              {isNotes && (
                <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground">
                  Antes: {entry.fromValue ?? '(vazio)'}
                </p>
              )}

              {entry.reason && (
                <p className="text-xs text-muted-foreground">
                  Motivo: {entry.reason}
                </p>
              )}

              <p className="text-xs text-muted-foreground">
                {entry.actorName} · {new Date(entry.createdAt).toLocaleDateString('pt-BR', {
                  day: '2-digit',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ServiceOrderDetailPage() {
  const { osId } = useParams({ strict: false }) as { osId: string };
  const stageConfig = useStageConfig();
  const router = useRouter();
  const {
    os,
    isLoading,
    error: osError,
    updateStatus,
    isUpdatingStatus,
    updateItems,
    isUpdatingItems,
    updateStage,
    updatePaint,
    isUpdatingPaint,
    updateSlot,
    isUpdatingSlot,
    updateDeadline,
    isUpdatingDeadline,
    updateInternalNotes,
    isUpdatingInternalNotes,
    updateVehicle,
    isUpdatingVehicle,
    saveClaim,
    removeClaim,
    addInsurerPayment,
    removeInsurerPayment,
    isSavingClaim,
  } = useServiceOrder(osId);
  const canViewFinance = useCanViewFinance();
  // O que a pessoa pode NESTA oficina (Fase 3). Fora do caminho, some; no
  // caminho, fica desabilitado com o nome de quem pode (`WhoCanHint`).
  const can = useCan();
  const canExecute = can('os.executar');
  const auth = useAuth();
  useOSRealtime(auth.user?.workshopId ?? null);
  const auditLogQuery = useAuditLog(osId);
  const { uncancel, isUncancelling } = useUncancelOS(osId);

  // Tab state
  const [activeTab, setActiveTab] = useState<'details' | 'history'>('details');

  // Uncancel dialog state
  const [showUncancelDialog, setShowUncancelDialog] = useState(false);
  const [uncancelReason, setUncancelReason] = useState('');

  // Items editing state
  const [editingItems, setEditingItems] = useState(false);
  // `stockAvailable` é só da tela (teto da quantidade); o resto é o item
  // como a API o conhece.
  const [draftItems, setDraftItems] = useState<DraftItem[]>([]);
  const [stockPickerOpen, setStockPickerOpen] = useState(false);

  // Quote state
  const [creatingQuote, setCreatingQuote] = useState(false);

  // Media
  const mediaQuery = useQuery<{ media: MediaItem[]; checklistPhotos?: ChecklistPhotoMeta[] }>({
    queryKey: ['os-media', osId],
    queryFn: async () => {
      const res = await api.get(`/media/${osId}`);
      return res.data;
    },
    enabled: !!osId,
    // Vídeo da galeria em conversão no servidor: relê até ficar pronto (leva
    // de segundos a poucos minutos). Sem nenhum, não relê.
    refetchInterval: (query) =>
      query.state.data?.media.some((m) => m.processing === 'processing') ? 15_000 : false,
  });

  const startEditItems = useCallback(() => {
    if (!os) return;
    setDraftItems(
      (os.items ?? []).map((it) => ({
        // Espalhar antes de sobrescrever o preço preserva `origin`,
        // `inventoryItemId` e `code`. Listar campo a campo, como era aqui,
        // apagava o vínculo com o estoque de toda OS aberta pelo app assim
        // que o gestor abrisse a edição de itens no painel — e a peça
        // silenciosamente deixava de baixar na conclusão.
        ...it,
        unitPrice: it.unitPrice / 100,
      })),
    );
    setEditingItems(true);
  }, [os]);

  const cancelEditItems = useCallback(() => {
    setEditingItems(false);
    setDraftItems([]);
  }, []);

  const saveItems = useCallback(async () => {
    await updateItems(
      draftItems.map(({ stockAvailable: _ignored, panel, operation, partOrigin, ...it }) => ({
        ...it,
        // "Nenhuma" no select é `''`: sai do item, em vez de gravar texto
        // vazio. Serviço sem peça não existe; procedência só em peça.
        ...(panel ? { panel } : {}),
        ...(panel && operation ? { operation } : {}),
        ...(it.type === 'part' && partOrigin ? { partOrigin } : {}),
        // Sinistro: "coberto" é o padrão, então só o recusado fica marcado.
        insuranceCoverage: undefined,
        ...(it.insuranceCoverage === 'declined' ? { insuranceCoverage: 'declined' as const } : {}),
        unitPrice: Math.round(it.unitPrice * 100),
      })),
    );
    setEditingItems(false);
    setDraftItems([]);
  }, [draftItems, updateItems]);

  const addDraftItem = useCallback(() => {
    setDraftItems((prev) => [...prev, { id: crypto.randomUUID(), type: 'labor', description: '', quantity: 1, unitPrice: 0 }]);
  }, []);

  const addStockPart = useCallback((part: PickedStockPart) => {
    setDraftItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'part',
        description: part.description,
        quantity: 1,
        unitPrice: part.unitPrice,
        code: part.code,
        origin: part.origin,
        inventoryItemId: part.inventoryItemId,
        stockAvailable: part.available,
      },
    ]);
  }, []);

  const removeDraftItem = useCallback((index: number) => {
    setDraftItems((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const updateDraftItem = useCallback((index: number, field: string, value: string | number) => {
    setDraftItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  }, []);

  // Trocar a peça do carro de um item: a descrição que o app escreveu
  // ("Reparar — Porta dianteira esquerda") acompanha a peça nova, como no
  // formulário do app. Sem isto o item ia para o bloco do capô dizendo "porta".
  // Descrição que o gestor escreveu fica como está.
  const changeDraftPanel = useCallback((index: number, panel: string) => {
    setDraftItems((prev) =>
      prev.map((item, i) => {
        if (i !== index) return item;
        const op = item.operation;
        const wasAuto =
          isVehiclePanel(item.panel) && isPanelOperation(op) && item.description === panelItemDescription(item.panel, op);
        const description =
          wasAuto && isVehiclePanel(panel) && isPanelOperation(op) ? panelItemDescription(panel, op) : item.description;
        return { ...item, panel, description };
      }),
    );
  }, []);

  const handleStatusChange = useCallback(async (newStatus: string) => {
    if (!os || newStatus === os.status) return;
    await updateStatus({ status: newStatus });
  }, [os, updateStatus]);

  const handleUncancel = useCallback(async () => {
    if (!uncancelReason.trim()) return;
    try {
      await uncancel({ reason: uncancelReason.trim() });
      setShowUncancelDialog(false);
      setUncancelReason('');
    } catch (err) {
      console.error('[OSDetail] uncancel failed', err);
    }
  }, [uncancelReason, uncancel]);

  // Quotes list
  const quotesQuery = useQuery<{ quotes: QuoteRow[] }>({
    queryKey: ['os-quotes', osId],
    queryFn: async () => {
      const res = await api.get(`/service-orders/${osId}/quotes`);
      return res.data;
    },
    enabled: !!osId,
  });

  const handleCreateQuote = useCallback(async () => {
    if (!os) return;
    setCreatingQuote(true);
    try {
      const res = await api.post(`/service-orders/${osId}/quotes`, { validityDays: 5 });
      const whatsapp = res.data.whatsapp as { sent: boolean; fallbackUrl?: string } | undefined;

      if (whatsapp?.sent) {
        // WhatsApp sent server-side — no deep link needed
      } else if (whatsapp?.fallbackUrl) {
        window.open(whatsapp.fallbackUrl, '_blank');
      } else {
        // Legacy fallback
        const phone = os.customerPhone?.replace(/\D/g, '');
        if (phone) {
          const msg = encodeURIComponent(
            `Orçamento da OS #${os.number} - ${os.vehicleName}: ${formatReais(Number(res.data.total))}`,
          );
          window.open(`https://wa.me/55${phone}?text=${msg}`, '_blank');
        }
      }

      // Refresh quotes list
      quotesQuery.refetch();
    } catch (err) {
      console.error('[OSDetail] create quote failed', err);
    } finally {
      setCreatingQuote(false);
    }
  }, [os, osId, quotesQuery]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 size={24} className="animate-spin text-muted-foreground" />
      </div>
    );
  }

  // 410: a OS foi excluída pelo suporte MustAuto. Vem antes do `!os` porque o
  // react-query mantém o dado da última busca boa — a tela aberta seguiria
  // mostrando a OS depois do evento `os-deleted`.
  const gone = (osError as { response?: { status?: number; data?: { error?: unknown } } } | null)?.response;
  if (gone?.status === 410) {
    return (
      <EmptyState
        icon={<Trash2 size={28} />}
        title="OS excluída"
        description={
          typeof gone.data?.error === 'string' ? gone.data.error : 'Esta OS foi excluída.'
        }
        action={{ label: 'Voltar', onClick: () => router.history.back() }}
      />
    );
  }

  if (!os) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
        <p>Ordem de serviço não encontrada.</p>
        <button
          className="mt-4 rounded-lg border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
          onClick={() => router.history.back()}
        >
          Voltar
        </button>
      </div>
    );
  }

  const statusCfg = STATUS_MAP[os.status];
  const isReadOnly = os.status === 'completed' || os.status === 'cancelled';
  const items = os.items ?? [];
  // Orçamento por peça (F3.3): com item de peça do carro, a tabela vem em
  // faixas por peça e "Outros itens" no fim; sem, a lista de sempre.
  const itemGroups: { key: string; label: string | null; byPanel: boolean; subtotal: number; items: OSItem[] }[] =
    hasPanelItems(items)
      ? panelItemGroups(items, (it) => it.total).map((g) => ({ ...g, byPanel: true }))
      : [{ key: 'all', label: null, byPanel: false, subtotal: 0, items }];
  // Peça do carro e procedência na edição: OS de funilaria, ou que já tem item de peça.
  const panelFields = os.module === 'funilaria' || hasPanelItems(items) || hasPanelItems(draftItems);
  const totalParts = Number(os.totalParts) || 0;
  const totalLabor = Number(os.totalLabor) || 0;
  const totalOutsourced = Number(os.totalOutsourced) || 0;
  const totalGeral = totalParts + totalLabor + totalOutsourced;
  const allMedia = mediaQuery.data?.media ?? [];
  const checklistMeta = mediaQuery.data?.checklistPhotos ?? [];
  // Fotos do checklist saem do grid genérico — ganham seção própria com as
  // marcações de dano. Entradas markup: são o raster antigo, também ficam fora.
  // Regra de @mustauto/utils — esta tela tinha a própria cópia, só com os seis
  // ângulos e `markup:`, e mostraria foto de painel no grid de execução.
  const nonChecklistMedia = allMedia.filter((m) => !isChecklistRef(m.itemRef));
  // Antes/depois da estética (F2.4): o par ganha card próprio, e nem ele nem a
  // foto substituída pelo "Refazer" se repetem no grid genérico.
  const { pairs: beforeAfterPairs, solo: media } = splitBeforeAfter(nonChecklistMedia);
  // Foto de peça (`panel:`) também sai do grid, mas não tem entrada no jsonb
  // de ângulos — sem entrar aqui ela sumiria da tela inteira. Entra no fim da
  // seção, com a marcação e a anotação que moram na própria linha da mídia
  // (F3.2; ver a migração 0102).
  const checklistPhotos: { meta: ChecklistPhotoMeta; item: MediaItem }[] = [
    ...checklistMeta
      .map((meta) => ({
        meta,
        item: allMedia.find((m) => m.itemRef === meta.angle && m.type === 'photo'),
      }))
      .filter((x): x is { meta: ChecklistPhotoMeta; item: MediaItem } => !!x.item),
    ...allMedia
      .filter((m) => isPanelRef(m.itemRef) && m.type === 'photo')
      .map((item) => ({
        meta: {
          angle: item.itemRef ?? '',
          markingsJson: item.markingsJson ?? undefined,
          annotationText: item.annotationText ?? undefined,
        },
        item,
      })),
  ];

  return (
    <div className="flex h-[calc(100vh-3rem)] gap-6">
      {/* Left: OS info */}
      <div className="flex-1 space-y-6 overflow-y-auto pr-2">
        <Breadcrumbs
          items={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Ordens de Serviço', href: '/service-orders' },
            { label: `OS #${os.number}` },
          ]}
        />

        {/* Tabs */}
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          <button
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'details'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('details')}
          >
            Detalhes
          </button>
          <button
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'history'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setActiveTab('history')}
          >
            Histórico
          </button>
        </div>

        {activeTab === 'history' && (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Clock size={16} />
                    Histórico de Alterações
                  </CardTitle>
                  {os.status === 'cancelled' && can('os.reabrir') && (
                    <button
                      className="flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600 disabled:opacity-50"
                      disabled={isUncancelling}
                      onClick={() => setShowUncancelDialog(true)}
                    >
                      {isUncancelling ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <RotateCcw size={12} />
                      )}
                      Recuperar OS
                    </button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {auditLogQuery.isLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 size={20} className="animate-spin text-muted-foreground" />
                  </div>
                ) : auditLogQuery.isError && !auditLogQuery.data ? (
                  /* "Nenhum registro de histórico" numa OS que tem registro é
                     a pior frase possível numa trilha de auditoria. */
                  <ErrorState
                    title="Não foi possível carregar o histórico"
                    error={auditLogQuery.error}
                    onRetry={() => auditLogQuery.refetch()}
                  />
                ) : (
                  <AuditTimeline entries={auditLogQuery.data?.entries ?? []} />
                )}
              </CardContent>
            </Card>

            {/* Uncancel dialog */}
            {showUncancelDialog && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                <div className="w-full max-w-md rounded-xl bg-background p-6 shadow-xl">
                  <h3 className="text-lg font-semibold">Recuperar OS #{os.number}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    A OS voltará para o status "Em Análise". Informe o motivo da recuperação.
                  </p>
                  <textarea
                    className="mt-4 block w-full rounded-lg border border-input bg-background p-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Motivo da recuperação..."
                    rows={3}
                    value={uncancelReason}
                    onChange={(e) => setUncancelReason(e.target.value)}
                  />
                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      className="rounded-md border px-4 py-2 text-sm hover:bg-muted"
                      onClick={() => {
                        setShowUncancelDialog(false);
                        setUncancelReason('');
                      }}
                    >
                      Cancelar
                    </button>
                    <button
                      className="flex items-center gap-1.5 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 dark:bg-amber-700 dark:hover:bg-amber-600 disabled:opacity-50"
                      disabled={!uncancelReason.trim() || isUncancelling}
                      onClick={handleUncancel}
                    >
                      {isUncancelling && <Loader2 size={12} className="animate-spin" />}
                      Confirmar Recuperação
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'details' && (
        <>
        {/* Header card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">OS #{os.number}</CardTitle>
              <div className="flex flex-wrap items-center justify-end gap-2">
                <PrintOSButton osId={os.id} osNumber={os.number} />
                {os.reopen && can('os.reabrir') && (
                  <ReopenOSButton osId={os.id} osNumber={os.number} reopen={os.reopen} />
                )}
                {/* Concluída é só leitura — o seletor some, e a entrega sai daqui. */}
                {os.status === 'completed' && <DeliverOSButton osId={os.id} osNumber={os.number} plate={os.plate} />}
                {!isReadOnly ? (
                  <select
                    className="h-8 rounded-md border border-input bg-background px-2 text-sm"
                    value={os.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={isUpdatingStatus}
                  >
                    {STATUS_OPTIONS.map((s) => {
                      const cfg = STATUS_MAP[s];
                      // Cancelar, concluir, entregar: cada um tem a sua
                      // permissão — a mesma regra da API.
                      const allowed = s === os.status || statusChangePermissions(os.status, s).every((p) => can(p));
                      return (
                        <option key={s} value={s} disabled={!allowed}>
                          {cfg?.label ?? s}{allowed ? '' : ' (sem permissão)'}
                        </option>
                      );
                    })}
                  </select>
                ) : statusCfg ? (
                  <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                ) : (
                  <Badge variant="outline">{os.status}</Badge>
                )}
                {isUpdatingStatus && <Loader2 size={14} className="animate-spin" />}
              </div>
            </div>
            {os.status === 'completed' && !can('os.entregar') && (
              <WhoCanHint permission="os.entregar" action="entrega o veículo" />
            )}
            <OSStageSelect
              module={os.module}
              stage={os.stage}
              closed={isOSClosed(os.status) || !canExecute}
              onChange={updateStage}
              // Sem a lista de mídia (carregando ou falhou) não dá para saber:
              // a opção fica livre, e a API recusa explicando se faltar a foto.
              forecast={os.stageForecast ?? null}
              hasStagePhoto={
                mediaQuery.isSuccess
                  ? (s) => allMedia.some((m) => m.type === 'photo' && m.itemRef === stageMediaRef(s))
                  : undefined
              }
            />
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <OSVehicleFields
                plate={os.plate}
                make={os.make}
                model={os.model}
                yearModel={os.year}
                // Quem cadastra o carro na abertura é quem corrige (a Recepção também).
                canEdit={can('os.abrir') && !auth.isDemo}
                onSave={updateVehicle}
                isSaving={isUpdatingVehicle}
              />
              {os.mechanicName && (
                <div>
                  <p className="text-xs text-muted-foreground">Mecânico</p>
                  <p className="font-medium">{os.mechanicName}</p>
                </div>
              )}
              {os.customerName && (
                <div>
                  <p className="text-xs text-muted-foreground">Cliente</p>
                  <p className="font-medium">{os.customerName}</p>
                </div>
              )}
              <OSDeadlineField
                osId={os.id}
                deadline={os.deadline}
                closed={isOSClosed(os.status) || !canExecute}
                onSave={updateDeadline}
                isSaving={isUpdatingDeadline}
              />
              {os.module && (
                <div>
                  <p className="text-xs text-muted-foreground">Tipo de serviço</p>
                  <p className="font-medium">{getModuleLabel(os.module)}</p>
                </div>
              )}
              {os.vehicleSize && (
                <div>
                  <p className="text-xs text-muted-foreground">Porte</p>
                  <p className="font-medium">{vehicleSizeLabel(os.vehicleSize)}</p>
                </div>
              )}
              {os.warranty && (
                <div>
                  <p className="text-xs text-muted-foreground">Garantia</p>
                  <p className="font-medium">
                    {os.warranty.until
                      ? `Até ${formatDateBR(os.warranty.until)}`
                      : `${formatWarrantyMonths(os.warranty.months)}, a contar da conclusão`}
                  </p>
                  {os.warranty.revisions.length > 0 && (
                    <p className="text-xs text-muted-foreground">Revisões: {warrantyRevisionDates(os.warranty)}</p>
                  )}
                </div>
              )}
              <OSPaintField
                paintColor={os.paintColor ?? null}
                paintCode={os.paintCode ?? null}
                paintFormula={os.paintFormula ?? null}
                vehiclePaintCode={os.vehiclePaintCode ?? null}
                alwaysShow={os.module === 'funilaria'}
                readOnly={!canExecute}
                onSave={updatePaint}
                isSaving={isUpdatingPaint}
              />
              <OSSlotField
                osId={os.id}
                slotId={os.slotId ?? null}
                slotLabel={os.slotLabel}
                closed={isOSClosed(os.status) || !canExecute}
                onSave={updateSlot}
                isSaving={isUpdatingSlot}
              />
              <div>
                <p className="text-xs text-muted-foreground">Aberta em</p>
                <p className="font-medium">
                  {new Date(os.createdAt).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
            </div>
            {os.notes && (
              <div className="mt-4 border-t pt-3">
                <p className="text-xs text-muted-foreground">Notas</p>
                <p className="mt-1 whitespace-pre-wrap break-words text-sm">{os.notes}</p>
              </div>
            )}

            {/* Invite link for B2C customer */}
            {os.vehicleId && (
              <div className="mt-4 border-t pt-3">
                <p className="mb-2 text-xs text-muted-foreground">Enviar histórico ao cliente</p>
                <InviteLinkButton vehicleId={os.vehicleId} />
              </div>
            )}
          </CardContent>
        </Card>

        <OSInternalNotesCard
          osId={os.id}
          internalNotes={os.internalNotes ?? null}
          locked={!canEditInternalNotes(os.status)}
          canExecute={canExecute}
          lastEdit={auditLogQuery.data?.entries.find((e) => e.action === 'internal_notes_updated') ?? null}
          onSave={updateInternalNotes}
          isSaving={isUpdatingInternalNotes}
        />

        {/* Items card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Itens do Serviço</CardTitle>
              {!isReadOnly && !editingItems && can('os.itens') && (
                <button
                  className="text-sm text-primary hover:underline"
                  onClick={startEditItems}
                >
                  Editar
                </button>
              )}
            </div>
            {!isReadOnly && !can('os.itens') && <WhoCanHint permission="os.itens" action="muda os itens" />}
          </CardHeader>
          <CardContent>
            {editingItems ? (
              <div className="space-y-3">
                {draftItems.map((item, i) => (
                  <div key={i} className="flex items-end gap-2">
                    <div className="w-28">
                      <select
                        className="block h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                        value={item.type}
                        onChange={(e) => updateDraftItem(i, 'type', e.target.value)}
                      >
                        <option value="part">Peça</option>
                        <option value="labor">Mão de obra</option>
                        <option value="outsourced">Terceirizado</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        className="block h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                        placeholder="Descrição"
                        value={item.description}
                        onChange={(e) => updateDraftItem(i, 'description', e.target.value)}
                      />
                    </div>
                    {panelFields && (
                      <div className="w-40">
                        <select
                          aria-label="Peça do carro"
                          className="block h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                          value={item.panel ?? ''}
                          onChange={(e) => changeDraftPanel(i, e.target.value)}
                        >
                          <option value="">Sem peça do carro</option>
                          {VEHICLE_PANEL_REGIONS.map((region) => (
                            <optgroup key={region} label={VEHICLE_PANEL_REGION_LABELS[region]}>
                              {VEHICLE_PANELS[region].map((panel) => (
                                <option key={panel} value={panel}>
                                  {getVehiclePanelLabel(panel)}
                                </option>
                              ))}
                            </optgroup>
                          ))}
                        </select>
                      </div>
                    )}
                    {panelFields && (
                      <div className="w-32">
                        {item.type === 'part' ? (
                          <select
                            aria-label="Procedência da peça"
                            className="block h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                            value={item.partOrigin ?? ''}
                            onChange={(e) => updateDraftItem(i, 'partOrigin', e.target.value)}
                          >
                            <option value="">Procedência…</option>
                            {PART_ORIGINS.map((origin) => (
                              <option key={origin} value={origin}>
                                {getPartOriginLabel(origin)}
                              </option>
                            ))}
                          </select>
                        ) : null}
                      </div>
                    )}
                    {os.insuranceClaim && (
                      <div className="w-32">
                        <select
                          aria-label="Cobertura da seguradora"
                          className="block h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                          value={item.insuranceCoverage === 'declined' ? 'declined' : 'covered'}
                          onChange={(e) => updateDraftItem(i, 'insuranceCoverage', e.target.value)}
                        >
                          <option value="covered">Seguro cobre</option>
                          <option value="declined">Recusado (cliente)</option>
                        </select>
                      </div>
                    )}
                    <div className="w-16">
                      <input
                        type="number"
                        // Óleo em litro, vitrificador em ml: a quantidade pode ser fração.
                        // `any` mantém as setas andando de 1 em 1.
                        min={0.001}
                        step="any"
                        // Peça do estoque não passa do saldo — mesma regra do
                        // app. Cobrar 5 de um item com 2 debitaria 2 e a
                        // diferença só apareceria no inventário.
                        max={item.origin === 'stock' ? item.stockAvailable : undefined}
                        className="block h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                        value={item.quantity}
                        // `max` não basta: sem `<form>`, a validação do HTML
                        // nunca roda e a quantidade acima do saldo era salva.
                        onChange={(e) => {
                          // Peça do estoque: 3 casas, como o débito — senão cobraria 1,2345 L
                          // e debitaria 1,235.
                          const typed =
                            item.origin === 'stock'
                              ? roundStockQuantity(Number(e.target.value))
                              : Number(e.target.value);
                          const cap = item.origin === 'stock' ? item.stockAvailable : undefined;
                          updateDraftItem(i, 'quantity', cap != null ? Math.min(typed, cap) : typed);
                        }}
                      />
                    </div>
                    <div className="w-24">
                      <input
                        type="number"
                        min={0}
                        step={0.01}
                        className="block h-8 w-full rounded-md border border-input bg-background px-2 text-xs disabled:opacity-60"
                        value={item.unitPrice}
                        // Sem `os.preco_manual`, o preço vem da tabela ou do
                        // estoque — não se digita.
                        disabled={!can('os.preco_manual')}
                        title={can('os.preco_manual') ? undefined : 'Você não pode digitar preço fora da tabela e do estoque'}
                        onChange={(e) => updateDraftItem(i, 'unitPrice', Number(e.target.value))}
                      />
                    </div>
                    <button
                      aria-label="Remover item"
                      className="text-muted-foreground hover:text-destructive"
                      onClick={() => removeDraftItem(i)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex flex-wrap items-center gap-4">
                  {/* Buscar no estoque primeiro: é o caminho que mantém o
                      vínculo com a peça e faz a baixa acontecer. */}
                  <button
                    type="button"
                    className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    onClick={() => setStockPickerOpen(true)}
                  >
                    <PackageSearch size={12} />
                    Buscar no estoque
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
                    onClick={addDraftItem}
                  >
                    <Plus size={12} />
                    Adicionar item à mão
                  </button>
                </div>

                {stockPickerOpen && (
                <StockPartPicker
                  onClose={() => setStockPickerOpen(false)}
                  onPick={addStockPart}
                  vehicleMake={os.make}
                  vehicleModel={os.model}
                />
                )}
                <div className="flex gap-2 border-t pt-3">
                  <button
                    className="flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    disabled={isUpdatingItems}
                    onClick={saveItems}
                  >
                    {isUpdatingItems ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
                    Salvar itens
                  </button>
                  <button
                    className="rounded-md border px-3 py-1.5 text-xs hover:bg-muted"
                    onClick={cancelEditItems}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                compact
                icon={<Package size={22} />}
                title="Nenhum item adicionado"
                description="Peças, mão de obra e terceirizados desta OS aparecem aqui."
              />
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead className="text-right">Qtd</TableHead>
                      <TableHead className="text-right">Unit.</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itemGroups.map((group) => (
                      <Fragment key={group.key}>
                        {group.label && (
                          // Orçamento por peça (F3.3): uma faixa por peça do
                          // carro, com o quanto ela custa. Mesma regra do app
                          // e do orçamento do cliente (`groupItemsByPanel`).
                          <TableRow className="bg-muted/50 hover:bg-muted/50">
                            <TableCell colSpan={4} className="text-xs font-semibold uppercase tracking-wide">
                              {group.label}
                            </TableCell>
                            <TableCell className="text-right text-xs font-semibold">
                              {formatCentavos(group.subtotal)}
                            </TableCell>
                          </TableRow>
                        )}
                        {group.items.map((item, i) => {
                          const typeLabel = item.type === 'part' ? 'Peça' : item.type === 'labor' ? 'M. obra' : 'Terceirizado';
                          const origin = group.byPanel ? partOriginText(item) : null;
                          return (
                            <TableRow key={`${group.key}-${i}`}>
                              <TableCell className="text-xs">{typeLabel}</TableCell>
                              <TableCell>
                                {group.byPanel ? panelItemLabel(item) : item.description}
                                {origin && <span className="block text-xs text-muted-foreground">{origin}</span>}
                                {os.insuranceClaim && item.insuranceCoverage === 'declined' && (
                                  <span className={`mt-0.5 inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${SOFT_BADGE_CLASSES.amber}`}>
                                    Recusado pela seguradora — cliente paga
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">{item.quantity}</TableCell>
                              <TableCell className="text-right">{formatCentavos(item.unitPrice)}</TableCell>
                              <TableCell className="text-right font-medium">{formatCentavos(item.total)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>

                {/* Totals */}
                <div className="mt-4 space-y-1 border-t pt-3 text-sm">
                  {totalParts > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Peças</span>
                      <span>{formatReais(totalParts)}</span>
                    </div>
                  )}
                  {totalLabor > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Mão de obra</span>
                      <span>{formatReais(totalLabor)}</span>
                    </div>
                  )}
                  {totalOutsourced > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Terceirizado</span>
                      <span>{formatReais(totalOutsourced)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-1 font-semibold">
                    <span>Total</span>
                    <span>{formatReais(totalGeral)}</span>
                  </div>
                  {/* Sinistro (Fase 4): o total não é o que o cliente paga. */}
                  {os.insuranceClaim && (
                    <>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Seguradora paga</span>
                        <span>{formatReais(os.insuranceClaim.insurerDue)}</span>
                      </div>
                      <div className="flex justify-between font-semibold">
                        <span>Cliente paga</span>
                        <span>{formatReais(os.totalDue ?? 0)}</span>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Payment info */}
        {os.paidAt && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Pagamento Registrado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">Valor Pago</p>
                  <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-300">
                    {formatReais(Number(os.paidAmount))}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Método</p>
                  <p className="font-medium">
                    {PAYMENT_METHOD_LABELS[os.paymentMethod ?? ''] ?? os.paymentMethod ?? '—'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Data</p>
                  <p className="font-medium">
                    {new Date(os.paidAt).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </p>
                </div>
                {os.slotLabel && (
                  <div>
                    <p className="text-xs text-muted-foreground">Vaga</p>
                    <p className="font-medium">{os.slotLabel}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sinistro de seguradora (Fase 4) — só para quem vê o financeiro. */}
        {canViewFinance && (
          <InsuranceClaimCard
            claim={os.insuranceClaim ?? null}
            canEdit={!auth.isDemo && os.status !== 'cancelled' && can('financeiro.lancar')}
            suggest={os.module === 'funilaria'}
            onSave={saveClaim}
            onRemove={removeClaim}
            onAddPayment={addInsurerPayment}
            onRemovePayment={removeInsurerPayment}
            saving={isSavingClaim}
          />
        )}

        {/* Laudo de espessura da pintura (F2.3) */}
        {os.paintReadings && <OSPaintReadingsCard readings={os.paintReadings} />}

        {/* Insumos do pacote (F2.6) */}
        {os.consumables && os.consumables.length > 0 && (
          <OSConsumablesCard
            osId={os.id}
            consumables={os.consumables}
            closed={isOSClosed(os.status) || os.consumablesLocked === true}
            canEdit={!auth.isDemo && canExecute}
          />
        )}

        {/* Media */}
        {checklistPhotos.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Image size={16} />
                Checklist de Entrada ({checklistPhotos.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {checklistPhotos.map(({ meta, item }) => {
                  const markings = parseMarkingsJson(meta.markingsJson);
                  const originLabel = mediaOriginLabel(item.source, item.takenAt, os.createdAt);
                  return (
                    <figure key={item.id}>
                      {/* A caixa reproduz o retângulo em que as marcações
                          foram desenhadas — a foto para marcação nova, o 4:3
                          legado para OS antiga (damageMarkingsAspect decide).
                          A foto vai em contain; cover desalinharia. */}
                      <div
                        className="group relative overflow-hidden rounded-lg border bg-muted"
                        style={{ aspectRatio: damageMarkingsAspect(markings) }}
                      >
                        <img
                          src={item.url}
                          alt={checklistLabel(meta.angle)}
                          className="absolute inset-0 h-full w-full object-contain"
                          loading="lazy"
                        />
                        <DamageMarkings markings={markings} />
                        <MediaOriginTag source={item.source} takenAt={item.takenAt} openedAt={os.createdAt} />
                        <MediaDownloadButton
                          mediaId={item.id}
                          label={checklistLabel(meta.angle)}
                        />
                      </div>
                      <figcaption className="mt-1 text-center text-xs text-muted-foreground">
                        {checklistLabel(meta.angle)}
                        {/* A foto do checklist é prova: a origem por extenso,
                            não só o selo. */}
                        {originLabel && <span className="block text-[11px]">{originLabel}</span>}
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        <OSBeforeAfterCard
          pairs={beforeAfterPairs}
          openedAt={os.createdAt}
          renderDownload={(photo, label) => <MediaDownloadButton mediaId={photo.id} label={label} />}
        />

        {media.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Image size={16} />
                Mídia ({media.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                {media.map((m) => (
                  <div key={m.id} className="group relative aspect-square overflow-hidden rounded-lg border bg-muted">
                    {m.type === 'photo' ? (
                      <img
                        src={m.url}
                        alt=""
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <MediaVideo
                        url={m.url}
                        posterUrl={m.posterUrl}
                        processing={m.processing}
                        className="h-full w-full object-cover"
                        placeholderClassName="text-muted-foreground"
                      />
                    )}
                    {/* Foto de etapa ("Antes da pintura", F3.4) e antes/depois
                        de peça desconhecida (versão futura) levam o nome. */}
                    {m.type === 'photo' && executionMediaLabel(m.itemRef, stageConfig) && (
                      <span className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1.5 py-1 text-[11px] font-medium text-white">
                        {executionMediaLabel(m.itemRef, stageConfig)}
                      </span>
                    )}
                    {m.visibility === 'internal' && (
                      <span className="absolute right-1 top-1 rounded bg-amber-500/80 px-1.5 py-0.5 text-[10px] font-medium text-white">
                        Interno
                      </span>
                    )}
                    {/* Acima da faixa do nome da etapa, quando ela existe; no vídeo,
                        em cima (embaixo estão os controles do player), abaixo
                        do botão de baixar. */}
                    <MediaOriginTag
                      source={m.source}
                      takenAt={m.takenAt}
                      openedAt={os.createdAt}
                      className={
                        m.type === 'video'
                          ? 'bottom-auto top-9'
                          : executionMediaLabel(m.itemRef, stageConfig)
                            ? 'bottom-7'
                            : undefined
                      }
                    />
                    <MediaDownloadButton
                      mediaId={m.id}
                      label={m.type === 'video' ? 'vídeo' : 'foto'}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quotes */}
        {(quotesQuery.data?.quotes?.length ?? 0) > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Orçamentos Enviados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {quotesQuery.data!.quotes.map((q) => {
                  const waBadge = q.whatsappStatus
                    ? WHATSAPP_STATUS_BADGE[q.whatsappStatus] ?? null
                    : null;
                  const recordedLine = quoteResponseRecordedLine(q.responseChannel, q.respondedByName);
                  // O cron só marca `expired` de hora em hora; entre um e
                  // outro, o pendente vencido não oferece "aprovou" (a API
                  // recusaria) — o caminho é gerar outro orçamento.
                  // OS fechada (concluída, entregue, cancelada) não recebe
                  // registro — mesma regra do app (`isOSClosed`).
                  const answerable =
                    q.status === 'pending' &&
                    !isOSClosed(os.status) &&
                    (!q.validUntil || new Date(q.validUntil) > new Date());
                  return (
                    <div key={q.id} className="flex items-center justify-between rounded-lg border p-3">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{formatReais(Number(q.total))}</span>
                          <Badge variant={q.status === 'approved' ? 'default' : q.status === 'rejected' ? 'destructive' : 'outline'}>
                            {QUOTE_STATUS_LABEL[q.status] ?? q.status}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Enviado em {q.sentAt ? new Date(q.sentAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                          {q.validUntil && ` · Válido até ${new Date(q.validUntil).toLocaleDateString('pt-BR')}`}
                        </p>
                        {recordedLine && <p className="text-xs text-muted-foreground">{recordedLine}</p>}
                        {answerable && (
                          <div className="pt-2">
                            {can('os.orcamento') ? (
                              <>
                                <p className="mb-2 text-xs text-muted-foreground">
                                  O cliente respondeu por WhatsApp, ligação ou no balcão? Registre aqui.
                                </p>
                                <QuoteResponseButtons osId={osId} quoteId={q.id} total={q.total} />
                              </>
                            ) : (
                              <WhoCanHint permission="os.orcamento" action="registra a resposta do cliente" />
                            )}
                          </div>
                        )}
                      </div>
                      {waBadge && (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${waBadge.className}`}>
                          {waBadge.icon} {waBadge.label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {!isReadOnly && items.length > 0 && (
          <div className="flex gap-3">
            <button
              className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 dark:bg-emerald-700 dark:hover:bg-emerald-600 disabled:opacity-50"
              disabled={creatingQuote}
              onClick={handleCreateQuote}
            >
              {creatingQuote ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Gerar Orçamento
            </button>
          </div>
        )}
        </>
        )}
      </div>

      {/* Right: Chat panel */}
      <div className="w-96 shrink-0">
        <ChatPanel osId={osId} readOnly={isReadOnly} />
      </div>
    </div>
  );
}
